// src/ui/AppearancePanel.jsx — this chat's theme, with a live preview.
//
// 🟢 THE SAME CHOICE AS THE PHONE, AND NOTHING MORE: one of eight themes or your own
//    photo, the doodle on or off, and the text size. A theme is a wallpaper and the bubble that suits it, in light
//    and in dark (./chatThemes.js, generated from the app), so there is nothing to
//    match by hand — the corner, dimming, tint and separate wallpaper grids the
//    panel used to carry are gone.
//
// 🟢 THE PREVIEW IS NOT A MOCKUP. It wears the exact classes the real pane wears
//    (apClasses), with the chat's real name and avatar. Changes also apply to the
//    real chat instantly (they're local and free), so the conversation beside the
//    panel is a second, full-size preview.

import React from 'react';
import Avatar from './Avatar';
import { X, Check } from './icons';
import { THEMES, TEXT_SIZES, apClasses, apStyle, photoToDataUrl } from '../services/appearance';

export default function AppearancePanel({ chat, chatTitle, profile, myUid, settings, onChange, onReset, onApplyAll, onClose }) {
  const set = (patch) => onChange({ ...settings, ...patch });
  const pv = apClasses(settings);
  const current = THEMES.find((t) => t.id === settings.theme) || THEMES[0];
  const usingPhoto = !!(settings.photo && settings.photoUrl);
  const fileRef = React.useRef(null);
  const [photoErr, setPhotoErr] = React.useState('');
  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    try {
      const photoUrl = await photoToDataUrl(f);
      setPhotoErr('');
      set({ photo: true, photoUrl });
    } catch { setPhotoErr('That picture could not be opened.'); }
  };
  /* 🖼️ the photo tile: chooses a picture the first time, then switches to it; once it is the
     wallpaper, choosing it again picks a different picture */
  const onPhotoTile = () => {
    if (settings.photoUrl && !usingPhoto) set({ photo: true });
    else if (fileRef.current) fileRef.current.click();
  };

  return (
    <div className="chat-tools">
      <div className="ct-head">
        <div className="ct-tabs"><button className="on">Chat theme</button></div>
        <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
      </div>

      <div className="ct-list">
        {/* ── the live preview: real name, real avatar, real CSS ── */}
        <div className={`ap-preview ${pv}`} style={apStyle(settings)}>
          <div className="ap-pv-head">
            <Avatar profile={profile} chat={chat} size="" myUid={myUid} />
            <div>
              <b>{chatTitle}</b>
              <i>online</i>
            </div>
          </div>
          <div className="ap-pv-msgs">
            <div className="ap-pv-day">Today</div>
            <div className="row in"><div className="bubble">Pick a theme below <span className="time">9:41</span></div></div>
            <div className="row out"><div className="bubble">Only you see this chat&apos;s theme. <span className="time">9:41</span></div></div>
          </div>
        </div>

        <div className="ap-section">
          <div className="ap-label">Theme · {usingPhoto ? 'Your photo' : current.name}</div>
          <div className="ap-themes" role="radiogroup" aria-label="Theme">
            {THEMES.map((t) => (
              <button
                key={t.id}
                role="radio"
                aria-checked={!usingPhoto && settings.theme === t.id}
                className={`ap-theme ${!usingPhoto && settings.theme === t.id ? 'on' : ''}`}
                title={t.name}
                aria-label={t.name}
                onClick={() => set({ theme: t.id, photo: false })}
              >
                {/* the wallpaper, with the bubble on it — the theme in one glance */}
                <span className="ap-theme-chip" style={{ '--chip-wall': t.light.bg, '--chip-wall-d': t.dark.bg, '--chip-out': t.light.out, '--chip-out-d': t.dark.out }}>
                  <span className="ap-theme-bubble" />
                </span>
                <span className="ap-theme-name">{t.name}</span>
              </button>
            ))}
            <button
              role="radio"
              aria-checked={usingPhoto}
              className={`ap-theme ${usingPhoto ? 'on' : ''}`}
              title={usingPhoto ? 'Choose another photo' : 'Your photo'}
              aria-label="Your photo"
              onClick={onPhotoTile}
            >
              <span className="ap-theme-chip ap-photo-chip" style={settings.photoUrl ? { backgroundImage: `url("${settings.photoUrl}")` } : undefined}>
                {!settings.photoUrl ? <span className="ap-photo-plus">+</span> : null}
              </span>
              <span className="ap-theme-name">Your photo</span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
          </div>
          {photoErr ? <div className="ap-note">{photoErr}</div> : null}
          {usingPhoto ? <div className="ap-note">Kept in this browser only. Click it again to choose another.</div> : null}
        </div>

        <div className="ap-section">
          <div className="ap-label">Text size</div>
          <div className="ap-seg" role="radiogroup" aria-label="Text size">
            {TEXT_SIZES.map((z) => (
              <button key={z.id} role="radio" aria-checked={(settings.size || 'normal') === z.id}
                className={(settings.size || 'normal') === z.id ? 'on' : ''} onClick={() => set({ size: z.id })}>
                {z.name}
              </button>
            ))}
          </div>
        </div>

        <div className="ap-section" hidden={usingPhoto}>
          <div className="ap-toggle-row">
            <div>Doodle<i>A faint sketch over the wallpaper, like the app</i></div>
            <button
              className={`ap-switch ${settings.doodle !== false ? 'on' : ''}`}
              onClick={() => set({ doodle: settings.doodle === false })}
              role="switch"
              aria-checked={settings.doodle !== false}
              aria-label="Doodle"
            />
          </div>
        </div>

        <div className="ap-actions">
          <button className="del-btn" onClick={onApplyAll}>
            <Check width={15} height={15} /> Apply to all chats
          </button>
          <button className="del-btn ghost" onClick={onReset}>Reset this chat</button>
        </div>
      </div>
    </div>
  );
}
